import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs, { Dayjs } from "dayjs";
import { BreadcrumbTexts } from "constant/BiPortalConstants";

export interface SelectedShiftData {
  shiftId?: number;
  blockId?: number;
  shiftName?: string;
  availableClientCount: number;
  availableDays: number[];
  startTime: string;
  endTime: string;
  blockIds?: string;
  daysOfWeek: string;
  daysOfWeekShort?: string;
  totalClientCount: number;
}

interface SendRequestDialogProps {
  open: boolean;
  onClose: () => void;
  requestType: string;
  selectedShifts: SelectedShiftData[];
  onSendRequest: (data: {
    startDate: string;
    isSchoolScheduleChange: string;
    additionalNotes: string;
  }) => void;
  isRemoval?: boolean;
  isSubmitting?: boolean;
  submitError?: string | null;
  onClearError?: () => void;
}

const expandDayAbbreviations = (schedule: string): string => {
  const parts = schedule.split(' ');
  if (parts.length === 0) return schedule;
  
  const daysPart = parts[0];
  const timePart = parts.slice(1).join(' ');
  
  const dayMap: { [key: string]: string } = {
    'M': 'Monday',
    'T': 'Tuesday',
    'W': 'Wednesday',
    'R': 'Thursday',
    'F': 'Friday',
    'S': 'Saturday',
    'U': 'Sunday'
  };
  
  const expandedDays: string[] = [];
  for (let i = 0; i < daysPart.length; i++) {
    const abbr = daysPart[i];
    if (dayMap[abbr]) {
      expandedDays.push(dayMap[abbr]);
    }
  }
  
  if (expandedDays.length > 0) {
    return `${expandedDays.join(', ')} ${timePart}`;
  }
  
  return schedule;
};

const SendRequestDialog: React.FC<SendRequestDialogProps> = ({
  open,
  onClose,
  requestType,
  selectedShifts,
  onSendRequest,
  isRemoval = false,
  isSubmitting = false,
  submitError = null,
  onClearError,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [isSchoolScheduleChange, setIsSchoolScheduleChange] = useState<string>("");
  const [additionalNotes, setAdditionalNotes] = useState<string>("");

  const handleSendRequest = () => {
    onSendRequest({
      startDate: startDate ? startDate.format("MM/DD/YYYY") : "",
      isSchoolScheduleChange,
      additionalNotes,
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setStartDate(null);
      setIsSchoolScheduleChange("");
      setAdditionalNotes("");
      onClose();
    }
  };


  const isFormValid = isRemoval || requestType === BreadcrumbTexts.additionalAvailability
    ? Boolean(startDate)
    : Boolean(startDate && isSchoolScheduleChange);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          padding: { xs: "16px", sm: "24px" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "20px", sm: "24px" },
            color: "#000000",
          }}
        >
          {isRemoval 
            ? "Send Remove Additional Availability Request to Supervisor"
            : `Send ${requestType === BreadcrumbTexts.setShift ? BreadcrumbTexts.setShift : BreadcrumbTexts.additionalAvailability} Request to Supervisor`
          }
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: "#666666",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ padding: 0 }}>
        {/* Error Alert */}
        {submitError && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              backgroundColor: "#FEF3F2",
              border: "1px solid #FEE4E2",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#D92D20",
              },
            }}
            icon={<ErrorOutlineIcon />}
            action={
              <IconButton
                size="small"
                onClick={onClearError}
                sx={{
                  color: "#000000",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#000000" }}>
              {submitError}
            </Typography>
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "14px", sm: "16px" },
              color: "#000000",
              mb: 1,
            }}
          >
            {isRemoval ? "You've selected to remove:" : "You've selected:"}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
            {selectedShifts.map((shift) => (
              <Box component="li" key={shift.shiftId} sx={{ mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "13px", sm: "14px" },
                    color: "#000000",
                  }}
                >
                  {isRemoval 
                    ? expandDayAbbreviations(shift.daysOfWeek)
                    : `${shift.daysOfWeek} ${shift.startTime} - ${shift.endTime}${shift.daysOfWeekShort ? ` (${shift.daysOfWeekShort.toUpperCase()})` : ''}`
                  }
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "14px", sm: "16px" },
              color: "#000000",
              mb: 1.5,
            }}
          >
            {isRemoval 
              ? "Effective date of removal"
              : `Start date of new ${requestType === BreadcrumbTexts.setShift ? BreadcrumbTexts.setShift : BreadcrumbTexts.additionalAvailability}`
            }
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="MM/DD/YYYY"
              minDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: "MM/DD/YYYY",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F5F5F5",
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#10389A",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#10389A",
                      },
                    },
                    "& input": {
                      fontSize: { xs: "14px", sm: "16px" },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {!isRemoval && requestType !== BreadcrumbTexts.additionalAvailability && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "14px", sm: "16px" },
                color: "#000000",
                mb: 1.5,
              }}
            >
              Are you requesting a shift change due to a change in your school schedule?
            </Typography>
            <RadioGroup
              value={isSchoolScheduleChange}
              onChange={(e) => setIsSchoolScheduleChange(e.target.value)}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    sx={{
                      color: "#666666",
                      "&.Mui-checked": {
                        color: "#10389A",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
                    Yes
                  </Typography>
                }
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    sx={{
                      color: "#666666",
                      "&.Mui-checked": {
                        color: "#10389A",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "14px", sm: "16px" },
              color: "#000000",
              mb: 1.5,
            }}
          >
            (Optional) Additional Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={1}
            placeholder="Add any additional notes for request"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#10389A",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#10389A",
                },
              },
              "& textarea": {
                fontSize: { xs: "14px", sm: "16px" },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: 0,
          mt: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            fontSize: { xs: "14px", sm: "16px" },
            color: "#10389A",
            fontWeight: 600,
            padding: { xs: "8px 16px", sm: "10px 24px" },
            "&:hover": {
              backgroundColor: "rgba(16, 56, 154, 0.04)",
            },
            "&.Mui-disabled": {
              color: "#9E9E9E",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSendRequest}
          disabled={!isFormValid || isSubmitting}
          endIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: "#FFFFFF" }} /> : <SendIcon />}
          sx={{
            textTransform: "none",
            fontSize: { xs: "14px", sm: "16px" },
            backgroundColor: (isFormValid && !isSubmitting) ? "#10389A" : "#E0E0E0",
            color: (isFormValid && !isSubmitting) ? "#FFFFFF" : "#9E9E9E",
            fontWeight: 600,
            padding: { xs: "8px 16px", sm: "10px 24px" },
            "&:hover": {
              backgroundColor: (isFormValid && !isSubmitting) ? "#0c2a75" : "#E0E0E0",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E0E0E0",
              color: "#9E9E9E",
            },
          }}
        >
          {isSubmitting ? "Sending..." : "Send Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendRequestDialog;

